import { useState, useEffect } from 'react';
import {
  Briefcase,
  Plus,
  Search,
  Users,
  Loader2,
  CheckCircle2,
  AlertCircle,
  FolderHeart,
} from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';

import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Badge } from '@/app/components/ui/badge';
import { Skeleton } from '@/app/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/app/components/ui/dialog';
import {
  useCrewTemplates,
  useCreateCrewTemplateFromJob,
  useApplyCrewTemplate,
} from '@/hooks/use-crew';
import { useProjects } from '@/hooks/use-projects';
import { handleApiError } from '@/lib/api-client';
import { toast } from '@/app/components/ui/simple-toaster';
import type { CrewTemplate, Project } from '@/lib/types';
import { usePermissions } from '@/hooks/use-permissions';

function useDebouncedValue<T>(value: T, delay = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

export function CrewTemplatesPage() {
  const { canManageOnboarding: canManageCrew } = usePermissions();
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const debouncedSearch = useDebouncedValue(search, 300);
  const { data, isLoading } = useCrewTemplates({ q: debouncedSearch || undefined });

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <FolderHeart className="h-6 w-6 text-blue-600" />
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Crew Templates</h1>
          </div>
          <p className="text-gray-600 mt-1">Save and reuse crew configurations from projects</p>
        </div>
        {canManageCrew && (
          <CreateTemplateDialog open={createOpen} onOpenChange={setCreateOpen} />
        )}
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          placeholder="Search templates..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
        />
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-40" />
          ))}
        </div>
      ) : !data || data.results.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Users className="h-12 w-12 text-gray-400 mb-4" />
            <p className="text-gray-600">No crew templates found</p>
            {canManageCrew && (
              <Button variant="outline" className="mt-4" onClick={() => setCreateOpen(true)}>
                Create your first template
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {data.results.map((template) => (
            <TemplateCard key={template.id} template={template} />
          ))}
        </div>
      )}
    </div>
  );
}

function TemplateCard({ template }: { template: CrewTemplate }) {
  const [applyOpen, setApplyOpen] = useState(false);

  return (
    <Card className="transition-shadow hover:shadow-md">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center justify-between">
          <span>{template.name}</span>
          <Badge variant="outline">{template.members?.length ?? 0} members</Badge>
        </CardTitle>
        {template.description ? (
          <p className="text-sm text-gray-600">{template.description}</p>
        ) : null}
      </CardHeader>
      <CardContent className="space-y-4">
        {template.members && template.members.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {template.members.slice(0, 6).map((member) => (
              <div
                key={member.id}
                className="flex items-center gap-1.5 bg-gray-50 rounded-full px-2 py-1"
              >
                <div className="h-5 w-5 rounded-full bg-blue-100 flex items-center justify-center">
                  <span className="text-blue-600 text-xs font-semibold">
                    {member.person_detail?.first_name?.[0] ??
                      member.person_detail?.full_name?.[0] ??
                      '?'}
                  </span>
                </div>
                <span className="text-xs font-medium">
                  {member.person_detail?.full_name ?? `Person ${member.person}`}
                </span>
              </div>
            ))}
            {template.members.length > 6 && (
              <span className="text-xs text-gray-500 self-center">
                +{template.members.length - 6} more
              </span>
            )}
          </div>
        ) : (
          <p className="text-sm text-gray-500">No members in this template</p>
        )}

        <div className="flex items-center justify-between text-sm text-gray-500">
          <span>Created by {template.created_by_username}</span>
          <ApplyTemplateDialog template={template} open={applyOpen} onOpenChange={setApplyOpen}>
            <Button size="sm" variant="outline" onClick={() => setApplyOpen(true)}>
              Apply to project
            </Button>
          </ApplyTemplateDialog>
        </div>
      </CardContent>
    </Card>
  );
}

function CreateTemplateDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const createMutation = useCreateCrewTemplateFromJob();
  const [name, setName] = useState('');
  const [projectSearch, setProjectSearch] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const debouncedProjectSearch = useDebouncedValue(projectSearch, 300);

  const { data: projectsData, isLoading: projectsLoading } = useProjects(
    { search: debouncedProjectSearch || undefined, page_size: 20 },
    true
  );

  const projects = projectsData?.results ?? [];

  const resetForm = () => {
    setName('');
    setSelectedProjectId(null);
    setProjectSearch('');
    setError(null);
  };

  const handleCreate = async () => {
    setError(null);
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError('Name is required.');
      return;
    }
    if (!selectedProjectId) {
      setError('Select a project to copy the crew from.');
      return;
    }
    try {
      await createMutation.mutateAsync({
        name: trimmedName,
        project_id: selectedProjectId,
      });
      toast.success('Crew template created.');
      onOpenChange(false);
      resetForm();
    } catch (err) {
      setError(handleApiError(err).message);
    }
  };

  const handleOpenChange = (openValue: boolean) => {
    if (!openValue) {
      resetForm();
    }
    onOpenChange(openValue);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          Create Template
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Create Crew Template from Project</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {error && <div className="bg-red-50 text-red-700 p-2 rounded text-sm">{error}</div>}
          <div className="space-y-2">
            <Label htmlFor="template-name">Template name *</Label>
            <Input
              id="template-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Tower Crew A"
            />
          </div>
          <div className="space-y-2">
            <Label>Source project *</Label>
            <Input
              placeholder="Search projects..."
              value={projectSearch}
              onChange={(e) => setProjectSearch(e.target.value)}
              className="mb-2"
            />
            <div className="border rounded-lg divide-y max-h-48 overflow-y-auto">
              {projectsLoading ? (
                <div className="p-4 text-center text-sm text-gray-500">
                  <Loader2 className="h-4 w-4 animate-spin mx-auto" />
                </div>
              ) : projects.length === 0 ? (
                <div className="p-4 text-center text-sm text-gray-500">No projects found</div>
              ) : (
                projects.map((project) => (
                  <ProjectRow
                    key={project.id}
                    project={project}
                    selected={selectedProjectId === project.id}
                    onClick={() => setSelectedProjectId(project.id)}
                  />
                ))
              )}
            </div>
          </div>
          <Button onClick={handleCreate} disabled={createMutation.isPending} className="w-full">
            {createMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Create Template
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ProjectRow({
  project,
  selected,
  onClick,
}: {
  project: Project;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full flex items-center justify-between p-3 text-left hover:bg-gray-50 ${
        selected ? 'bg-blue-50' : ''
      }`}
    >
      <div className="flex items-center gap-2">
        <Briefcase className="h-4 w-4 text-gray-400" />
        <div>
          <p className="text-sm font-medium text-gray-900">{project.name}</p>
          {project.project_number ? (
            <p className="text-xs text-gray-500">{project.project_number}</p>
          ) : null}
        </div>
      </div>
      {selected && <CheckCircle2 className="h-4 w-4 text-blue-600" />}
    </button>
  );
}

function ApplyTemplateDialog({
  template,
  open,
  onOpenChange,
  children,
}: {
  template: CrewTemplate;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
}) {
  const applyMutation = useApplyCrewTemplate();
  const [projectSearch, setProjectSearch] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{
    assigned: number;
    skipped: number;
  } | null>(null);
  const debouncedProjectSearch = useDebouncedValue(projectSearch, 300);

  const { data: projectsData, isLoading: projectsLoading } = useProjects(
    { search: debouncedProjectSearch || undefined, page_size: 20 },
    true
  );

  const projects = projectsData?.results ?? [];

  const handleApply = async () => {
    setError(null);
    if (!selectedProjectId) {
      setError('Select a project to apply the template to.');
      return;
    }
    try {
      const response = await applyMutation.mutateAsync({
        id: template.id,
        data: { project_id: selectedProjectId },
      });
      setResult({ assigned: response.assigned.length, skipped: response.skipped.length });
      toast.success(
        `Applied ${template.name}: ${response.assigned.length} assigned, ${response.skipped.length} skipped`
      );
    } catch (err) {
      setError(handleApiError(err).message);
    }
  };

  const handleClose = (openValue: boolean) => {
    if (!openValue) {
      setResult(null);
      setError(null);
      setSelectedProjectId(null);
      setProjectSearch('');
    }
    onOpenChange(openValue);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Apply “{template.name}”</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {error && <div className="bg-red-50 text-red-700 p-2 rounded text-sm">{error}</div>}
          {result ? (
            <div className="bg-green-50 text-green-700 p-3 rounded text-sm space-y-1">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4" />
                <span className="font-medium">Template applied successfully</span>
              </div>
              <p>{result.assigned} crew members assigned</p>
              {result.skipped > 0 && (
                <p>{result.skipped} skipped (unavailable, missing cert, or invalid person)</p>
              )}
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <Label>Target project *</Label>
                <Input
                  placeholder="Search projects..."
                  value={projectSearch}
                  onChange={(e) => setProjectSearch(e.target.value)}
                  className="mb-2"
                />
                <div className="border rounded-lg divide-y max-h-48 overflow-y-auto">
                  {projectsLoading ? (
                    <div className="p-4 text-center text-sm text-gray-500">
                      <Loader2 className="h-4 w-4 animate-spin mx-auto" />
                    </div>
                  ) : projects.length === 0 ? (
                    <div className="p-4 text-center text-sm text-gray-500">No projects found</div>
                  ) : (
                    projects.map((project) => (
                      <ProjectRow
                        key={project.id}
                        project={project}
                        selected={selectedProjectId === project.id}
                        onClick={() => setSelectedProjectId(project.id)}
                      />
                    ))
                  )}
                </div>
              </div>
              {template.members.length === 0 && (
                <div className="flex items-start gap-2 text-amber-700 bg-amber-50 p-2 rounded text-sm">
                  <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>This template has no members, so applying it will not assign anyone.</span>
                </div>
              )}
            </>
          )}
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1" onClick={() => handleClose(false)}>
              {result ? 'Close' : 'Cancel'}
            </Button>
            {!result && (
              <Button
                className="flex-1"
                onClick={handleApply}
                disabled={applyMutation.isPending}
              >
                {applyMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Apply Template
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
